/**
 * The kit attachments' vertex motion (kit/kitMaterial.ts), on the GPU, per vertex, before skinning, in the piece's own
 * frame: uN up, oN out, rN right (bone space; `lu` is the bone's metre-to-local factor, so amplitudes are metres).
 * Order: breathe / twinkle / charge swell → flap about its hinge (or the strike) → cloth wave → roll → sway → spin → orbit → flow swell
 * → bob. Rows of the table: kitMaterial.ts packMotion.
 */
export const KIT_MOTION_GLSL = {
  head: (rows: number, stride: number): string => `
attribute vec3 kitPivot, kitUp, kitOut;
attribute vec2 kitData;
uniform vec4 uKitA[${rows}], uKitB[${rows}], uKitC[${rows}], uKitD[${rows}], uKitE[${rows}], uKitF[${rows}], uKitG[${rows}];
uniform float uKitTime, uKitMotion, uKitDetail, uKitCharge, uKitStrike;
varying float vKitGlow;
#ifdef KIT_INSTANCED
attribute float kitAlong, kitLit;   // road props: cells from the portal (the travel wave), lit by the enemies passing
#endif
vec3 tdKitPos;
const int TD_COPY_STRIDE = ${stride};
vec3 tdRot(vec3 v, vec3 k, float a) { float c = cos(a), s = sin(a); return v * c + cross(k, v) * s + k * dot(k, v) * (1.0 - c); }`,
  body: `
  {
    #ifdef KIT_INSTANCED
    vec3 kp = vec3(0.0), ku = vec3(0.0, 1.0, 0.0), ko = vec3(0.0, 0.0, 1.0), seed = instanceMatrix[3].xyz;
    #else
    vec3 kp = kitPivot, ku = kitUp, ko = kitOut, seed = kitPivot;
    #endif
    int code = int(kitData.x + 0.5);
    int i = code % TD_COPY_STRIDE;
    float copy = float(code / TD_COPY_STRIDE);
    float w = kitData.y;
    vec4 A = uKitA[i], B = uKitB[i], C = uKitC[i], D = uKitD[i], E = uKitE[i], F = uKitF[i], G = uKitG[i];
    float ph = fract(sin(dot(seed, vec3(12.9898, 78.233, 37.719))) * 43758.5453) * 6.2831853;
    #ifdef KIT_INSTANCED
    if (C.w != 0.0) ph = -C.w * kitAlong;                      // travel: the phase runs down the road, portal to fortress
    #else
    if (C.w != 0.0) ph = -C.w * (seed.x + seed.z);             // travel: the phase runs along the ground
    #endif
    float t = uKitTime * uKitMotion, om = B.x * 6.2831853, ch = uKitCharge * uKitMotion;
    float lu = max(length(ku), 1e-4);
    vec3 uN = ku / lu, oN = normalize(ko), rN = normalize(cross(uN, oN));
    vec3 rest = position - kp, p = rest, n = objectNormal;
    // breathing scale, the charge swell, the twinkle (grow from nothing and back) and the detail gate
    float sc = (1.0 + A.w * sin(t * om + ph)) * (1.0 + G.z * ch);
    if (C.x > 0.0) sc *= uKitMotion > 0.5 ? max(0.0, sin(uKitTime * C.x * 6.2831853 + ph)) : 0.7;
    sc *= mix(1.0, uKitDetail, C.y);
    p *= sc;
    // flap: rocks about a hinge on its axis; mirrored, the left half turns the other way about its own hinge; driven by
    // the charge (mode >= 8), the angle follows the tower's strike (0 resting at the bias .. 1 raised by deg)
    if (E.x != 0.0 || E.y != 0.0) {
      int mode = int(E.w + 0.5), ax = mode % 4;
      vec3 fa = ax == 0 ? oN : (ax == 1 ? rN : uN);
      float side = (mode % 8 >= 4 && dot(rest, rN) < 0.0) ? -1.0 : 1.0;
      float fdrive = mode >= 8 ? uKitStrike * uKitMotion : sin(t * G.w * 6.2831853 + ph);
      float fang = (E.y + E.x * fdrive) * side;
      vec3 hinge = rN * lu * E.z * side;
      p = hinge + tdRot(p - hinge, fa, fang); n = tdRot(n, fa, fang);
    }
    // cloth: a travelling wave along the piece's right axis, out of its plane, by the authored weight
    float along = dot(p, rN) / lu;
    p += oN * lu * B.y * w * sin(t * om * 2.2 - along * B.z + ph);
    float rl = C.z * t + ph * step(1e-4, abs(C.z));
    p = tdRot(p, oN, rl); n = tdRot(n, oN, rl);
    float sw = A.z * sin(t * om + ph);
    p = tdRot(p, rN, sw); n = tdRot(n, rN, sw);
    float sp = A.x * t + ph * step(1e-4, abs(A.x));
    p = tdRot(p, uN, sp); n = tdRot(n, uN, sp);
    // orbit: the piece stands its radius out already (baked), and turns round the up axis (copies evenly spaced),
    // the orbit's plane tilted about right
    if (D.x != 0.0) {
      float oa = D.y * t + copy * 6.2831853 / max(D.w, 1.0) + ph;
      p = tdRot(p, uN, oa); n = tdRot(n, uN, oa);
      p = tdRot(p, rN, D.z); n = tdRot(n, rN, D.z);
    }
    // flow: bands travelling along an axis of the piece, brightening the emissive and swelling the surface
    float flow = 0.0;
    if (F.x != 0.0 || F.z != 0.0 || F.w != 0.0) {
      int fx = int(G.x + 0.5);
      vec3 dir = fx == 0 ? uN : (fx == 1 ? -uN : (fx == 2 ? rN : (fx == 3 ? -rN : oN)));
      float band = 0.5 + 0.5 * sin(6.2831853 * (dot(rest, dir) / lu - F.x * t) / F.y + ph);
      flow = F.z * band;
      p += normalize(n) * lu * F.w * (band * 2.0 - 1.0) * uKitMotion;
    }
    tdKitPos = kp + p + uN * lu * (A.y * sin(t * om + ph));
    objectNormal = n;
    vKitGlow = (1.0 + B.w * sin(t * om * 1.3 + ph)) * (1.0 + flow) * (1.0 + G.y * ch);
    #ifdef KIT_INSTANCED
    vKitGlow *= 1.0 + kitLit * uKitMotion;
    #endif
  }`,
} as const;
